import React, { useContext } from 'react';
import styled from 'styled-components';
import { t } from '@lingui/macro';
import { useState } from 'react';
import { LangContext } from '~/components/../providers/LangProvider';
import { getUTMParams } from '~/components/UTMParams';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import LoadingSplash from '~/components/loadingSplash/LoadingSplash';
import { WelcomeWrapper } from '~/styles/welcome';
import { YellowButton } from '~/styles/buttons';
import { tabletBreakpoint } from '~/styles/common';
import { Trans, withI18n } from '@lingui/react';
import { Form } from 'formik';
import { Formik } from 'formik';
import TransHTML from '~/components/transHTML';
import ClassicInput from '~/components/inputs/ClassicInput';
import PhoneInput from '~/components/inputs/PhoneInput';
import SelectInput from '~/components/inputs/SelectInput';
import { sendGoogleScript } from './contact-form/contact-form';
import { getInvite } from "./referral";
import { leadFormSuccessRedirect } from "../services/google-analytics/lead-form-redirect.ts";
import { cleanPhoneNumber } from "../services/sanitaze/clean-phone-number.ts";
import LeadFormSent from "./business/lead-form/form-sent.tsx";
import { getValidation } from "./business/lead-form/validation.ts";
import { businessCategories } from "./business/lead-form/categories.ts";
import { tierModeOptions, tierNameOptions } from "./business/lead-form/tier-options";
import { countryOptions } from "./business/lead-form/country-options";
import { trisbeeAxios } from '../services/axios/trisbeeAxiosWeb';
import AlertMessage, {AlertType} from "./AlertMessage";

const { publicRuntimeConfig } = getConfig();

function LeadForm({ tierParams, i18n }) {
    const router = useRouter();
    const ctx = useContext(LangContext);
    const selectedCountry = ctx.state.country;
    const [startValidationOnChange, setStartValidationOnChange] = useState(false);

    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState(false);
    const [formErrorMessage, setFormErrorMessage] = useState(null);
    const [formSent, setFormSent] = useState(false);
    const [phoneValid, setPhoneValid] = useState(false);

    const handleSubmit = async (values) => {
        setLoading(true);
        const googleFormData = {
            name: values.firstName + ' ' + values.lastName,
            email: values.email,
            telephone: cleanPhoneNumber(values.phone.withPrefix),
            category: values.category,
            language: ctx.state.lang,
            country: values.country,
            tierMode: values.tierMode,
            tierName: values.tierName,
        };
        const apiFormData = {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            phoneNumber: cleanPhoneNumber(values.phone.withPrefix),
            businessCategory: values.category,
            language: ctx.state.lang,
            country: values.country,
            tierMode: values.tierMode,
            tierName: values.tierName,
        };
        await sendGoogleData(googleFormData);
        sendToApiTimeout(apiFormData);
        setLoading(false);
    };

    const errorMessage = {
        missing_configuration: t`leadForm.error.missing_config`,
        basic_error: "Something went wrong, please try again"
    };

    const incorrectCountry = (country) => {
        if (!publicRuntimeConfig.FORM.onboarding.hasOwnProperty(country)) {
            console.error(`Missing form config for country: ${country}`);
            setFormError(true);
            setFormErrorMessage(errorMessage.missing_configuration);
            return true;
        }
        return false;
    }

    const sendGoogleData = async (formData) => {

        // Ensure that lang was not changed during fulfilling the form
        formData.language = ctx.state.lang;

        if (incorrectCountry(formData.country)) {
            return;
        }

        const formConfig = publicRuntimeConfig.FORM.onboarding[formData.country];

        //
        // append marketing UTM tracking (stored in ./pages/_app.js) and current URL where form is placed
        // @logic: order of keys is reflected in sent email
        //
        const formDataNameOrder = Object.assign({}, formData, getUTMParams());

        const response = await sendGoogleScript(
            formConfig.googleScript,
            formDataNameOrder,
            'successSendContactForm'
        );

        if (response.sent) {
            leadFormSuccessRedirect(formData.country);
        }

        setFormError(response.error);
        setFormErrorMessage(errorMessage.basic_error)
        setFormSent(response.sent);
    };

    const initialValues = {
        country: `${ctx.state.country.toUpperCase()}`,
        tierMode: tierParams.tierMode,
        tierName: tierParams.tierName,
    };

    /**
    * submission to our API with timeout 1 second - to let GA know about submitted form (leadFormSuccessRedirect)
    */
    const sendToApiTimeout = (formData) => {
        let timer = setTimeout(() => {
            trisbeeAxios.post('/merchant/lead/create', formData).then((res) => {
                // Redirect to Trisbee Business onboarding
                router.push(res.data.redirectToUrl);
            }).catch((err) => {
                setFormError(err);
                setFormErrorMessage(errorMessage.basic_error);
            })
            clearTimeout(timer);
        }, 1000);
    }

    return (
        <LeadFromWelcomeWrapper id="welcome">
            <LeadFormWrapper>
                <AlertMessage type={AlertType.warning}>
                    <Trans id={"global.registration.paused"} />
                </AlertMessage>
            </LeadFormWrapper>
        </LeadFromWelcomeWrapper>
    )

    return (
        <LeadFromWelcomeWrapper
            id="welcome"
        >
            {formSent && (
                <LeadFormSent />
            )}
            {!formSent && (
                <LeadFormWrapper>
                    <FormWrapper>
                        <LeadFormHeader>
                            <Trans id="global.join_us.leadForm.heading" />
                        </LeadFormHeader>
                        <LoadingSplash
                            isLoading={loading}
                            overlay={false}
                        >
                            {formError && (
                                <div>
                                    <ErrorMsg>{formErrorMessage}</ErrorMsg>
                                </div>
                            )}
                            <Formik
                                validationSchema={getValidation(i18n)}
                                validateOnChange={startValidationOnChange}
                                validateOnBlur={startValidationOnChange}
                                validate={values => {
                                    setStartValidationOnChange(true);

                                    let errors = {};
                                    if (!phoneValid) {
                                        errors.phone = i18n._('contactForm.phone.required');
                                    }

                                    return errors;
                                }}
                                initialValues={initialValues}
                                onSubmit={handleSubmit}
                            >
                                {({ errors, touched }) => (
                                    <FormikForm>
                                        <ClassicInput
                                            name={'firstName'}
                                            touched={touched}
                                            errors={errors}
                                            label={i18n._('contactForm.firstName.label')}
                                            isFocused={true}
                                        />
                                        <ClassicInput
                                            name={'lastName'}
                                            touched={touched}
                                            errors={errors}
                                            label={i18n._('contactForm.lastName.label')}
                                        />
                                        <ClassicInput
                                            name={'email'}
                                            touched={touched}
                                            errors={errors}
                                            label={i18n._('contactForm.email.label')}
                                        />
                                        <PhoneInput
                                            name={'phone'}
                                            touched={touched}
                                            errors={errors}
                                            setPhoneValid={setPhoneValid}
                                            phoneErrorMessage={i18n._(
                                                'contactForm.phone.required'
                                            )}
                                            label={i18n._('contactForm.phone.label')}
                                        />
                                        <SelectInput
                                            name={'country'}
                                            touched={touched}
                                            errors={errors}
                                            options={countryOptions}
                                            defaultValue={selectedCountry}
                                            label={i18n._('onboarding.country.label')}
                                        />
                                        <SelectInput
                                            hasTranslations={true}
                                            name={'category'}
                                            touched={touched}
                                            errors={errors}
                                            options={businessCategories}
                                            label={i18n._('onboarding.category.label')}
                                        />
                                        <SelectInput
                                            hidden={true}
                                            hasTranslations={true}
                                            name={'tierName'}
                                            touched={touched}
                                            errors={errors}
                                            options={tierNameOptions}
                                            label={i18n._('onboarding.tierName.label')}
                                        />
                                        <SelectInput
                                            hidden={true}
                                            hasTranslations={true}
                                            name={'tierMode'}
                                            touched={touched}
                                            errors={errors}
                                            options={tierModeOptions}
                                            label={i18n._('onboarding.tierMode.label')}
                                        />
                                        <CustomSubmitButton type="submit">
                                            <Trans id="global.join_us.leadForm.submit" />
                                        </CustomSubmitButton>
                                    </FormikForm>
                                )}
                            </Formik>
                        </LoadingSplash>
                    </FormWrapper>
                    <InformationWrapper>
                        <PointWrapper>
                            <PointImg src="/static/images/new_web/green_tick.svg" />
                            <PointText>
                                <TransHTML
                                    id={t`global.join_us.leadForm.point1`}
                                />
                            </PointText>
                        </PointWrapper>
                        <PointWrapper>
                            <PointImg src="/static/images/new_web/green_tick.svg" />
                            <PointText>
                                <TransHTML
                                    id={t`global.join_us.leadForm.point2`}
                                />
                            </PointText>
                        </PointWrapper>
                        <PointWrapper>
                            <PointImg src="/static/images/new_web/green_tick.svg" />
                            <PointText>
                                <TransHTML
                                    id={t`global.join_us.leadForm.point3`}
                                />
                            </PointText>
                        </PointWrapper>
                    </InformationWrapper>
                </LeadFormWrapper>
            )}
        </LeadFromWelcomeWrapper>
    );
}

export default withI18n()(LeadForm);

const ErrorMsg = styled.h5`
    color: red;
`;

const LeadFormWrapper = styled.div`
    max-width: 860px;
    width: 100%;
    margin: 0 auto;
    background-color: white;
    padding: 56px;
    box-shadow: 0 0 33px #e9edec;
    border-radius: 8px;
    display: flex;
    flex-direction: row;
    justify-content: space-around;

    @media (max-width: ${tabletBreakpoint}) {
        flex-direction: column;
        padding: 15px;
        align-items: center;
    }

    .MuiFormControl-root {
        width: 100%;
        height: 55px;
    }
`;

const FormWrapper = styled.div`
    margin-right: 56px;
    max-width: 375px;
    width: 100%;

    @media (max-width: ${tabletBreakpoint}) {
        margin-right: 0;
    }
`;

const InformationWrapper = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 70px;
    min-width: 320px;
`;

const LeadFromWelcomeWrapper = styled(WelcomeWrapper)`
    padding-bottom: 87px;
    padding-top: 75px;
    height: 100% !important;

    @media (max-width: ${tabletBreakpoint}) {
        height: 100% !important;
    }
`;

const LeadFormHeader = styled.div`
    font-style: normal;
    font-weight: bold;
    font-size: 35px;
    line-height: 56px;
    letter-spacing: -0.25px;
    color: #0cc8a8;
    margin-bottom: 16px;
    text-align: center;
`;

const PointWrapper = styled.div`
    display: flex;
    margin-bottom: 50px;

    @media (max-width: 400px) {
        margin-left: 30px;
    }
`;

const PointImg = styled.img`
    margin-right: 26px;
    width: 32px;
`;

const PointText = styled.div`
    font-style: normal;
    font-size: 24px;
    line-height: 36px;
    letter-spacing: -0.25px;
    color: #2d2d2d;

    @media (max-width: ${tabletBreakpoint}) {
        font-size: 18px;
    }
`;

const FormikForm = styled(Form)`
    width: 100%;
`;

const CustomSubmitButton = styled(YellowButton)`
    width: 100%;
    margin-top: 16px;
`;
